import { AppMenuListItemProps } from '@components/AppMenuListItem/AppMenuListItem';
import { FederatedErrorBoundary } from '@components/RemoteMicroFrontends/FederatedErrorBoundary';
import { HeaderFallback } from '@components/RemoteMicroFrontends/fallbacks';
import { DISCOVERY_ADMIN_URL } from '@constants/externalRoutes';
import { BURGER_MENU_ADMIN_OPTIONS } from '@constants/menus';
import { useAuthStoreContext } from '@context/authStore';
import useIsRootAdmin from '@hooks/useIsRootAdmin';
import { CoreBox } from '@youscience/core';
import { ErrorInfo, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { MenuLinks } from './MenuLinks';

import { sxStyles } from './Header.styles';
import './styles.scss';

const BrightpathHeader = lazy(() => import('brightpathHeader/Header'));

export const Header = () => {
  const { isLoadingUser, userData, accessDocumentId, isImpersonated, setUserPreferences, getUserPreferences } =
    useAuthStoreContext();

  const isRootAdmin = useIsRootAdmin();

  const params = useParams();

  let options: AppMenuListItemProps[] = [];

  const handleTenantSelect = async (accessId: string) => {
    if (accessId !== accessDocumentId) {
      await setUserPreferences(accessId);
      // ? This is a temporary call, it helps to set x-current-access cookie
      await getUserPreferences();
    }
  };

  if (!isLoadingUser && isRootAdmin) {
    options = BURGER_MENU_ADMIN_OPTIONS;

    if (params.tenantId) {
      options = [
        ...options,
        {
          item: {
            title: 'Back to Tenant Page',
            key: 'back-to-tenant-page',
            path: `${DISCOVERY_ADMIN_URL}/tenants/${params.tenantId}`,
            icon: <i className="ys-icon-right-row" />,
            translateKey: 'back-to-tenant-page',
          },
        },
      ];
    }
  }

  return (
    <CoreBox className="c-header" component="header" sx={sxStyles.header}>
      <Suspense fallback={null}>
        <FederatedErrorBoundary
          moduleName="brightpathHeader"
          fallback={<HeaderFallback />}
          onError={(error: Error, errorInfo: ErrorInfo) => {
            console.error('Failed to load BrightpathHeader:', error, errorInfo);
          }}
        >
          <BrightpathHeader
            accessDocumentId={accessDocumentId}
            currentProductPage="education-career-plan"
            handleTenantSelectCallback={handleTenantSelect}
            impersonating={isImpersonated}
            productSpecificNode={<MenuLinks options={options} />}
            userDocument={userData}
          />
        </FederatedErrorBoundary>
      </Suspense>
    </CoreBox>
  );
};
